import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import { ApiClientQueryParams } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable, interval } from "rxjs";
import {
    catchError,
    finalize,
    map,
    mergeMap,
    take,
    takeWhile,
    tap,
} from "rxjs/operators";
import { create } from "superstruct";
import { injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import RewardForReviewEntity from "../entities/RewardForReviewEntity";
import RewardForReviewSchema from "../schemas/RewardForReviewSchema";
import RewardForReviewStore from "../stores/RewardForReviewStore";

@injectable()
class RewardForReviewService {
    constructor(
        private readonly rewardForReviewStore: RewardForReviewStore,
        private readonly authApiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}

    getRewardForReview(
        reviewId: number,
        reviewRequestToken?: string
    ): Observable<RewardForReviewEntity> {
        this.logger.debug(
            "RewardForReviewService.getRewardForReview is started",
            reviewId
        );
        this.rewardForReviewStore.setStatus(STATUS.LOADING);
        const queryParams: ApiClientQueryParams = { reviewId };

        if (reviewRequestToken) {
            queryParams.reviewRequestToken = reviewRequestToken;
        }

        return this.authApiClient
            .get({
                url: "/review/getRewardForReview",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((v) => create(v.body, RewardForReviewSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardForReviewService.getRewardForReview is errored ",
                        { reviewId, error }
                    );
                    throw error;
                }),
                tap((body) => {
                    this.logger.debug(
                        "RewardForReviewService.getRewardForReview is ready",
                        reviewId
                    );
                    applyTransaction(() => {
                        this.rewardForReviewStore.update((state) => ({
                            ...state,
                            reward: body.reward,
                        }));
                    });
                })
            );
    }

    getRewardForReviewByInterval(
        reviewId: number,
        reviewRequestToken?: string
    ): Observable<RewardForReviewEntity> {
        this.rewardForReviewStore.setStatus(STATUS.LOADING);
        return interval(400).pipe(
            take(6),
            map(() => this.rewardForReviewStore.getValue().reward),
            takeWhile((reward) => !reward),
            mergeMap(() =>
                this.getRewardForReview(reviewId, reviewRequestToken)
            ),
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.rewardForReviewStore.setStatus(STATUS.ERROR);
                    this.rewardForReviewStore.setError(error as Error);
                });
                throw error;
            }),
            finalize(() => this.rewardForReviewStore.setStatus(STATUS.READY))
        );
    }

    resetStore() {
        applyTransaction(() => {
            this.rewardForReviewStore.update({ reward: null });
            this.rewardForReviewStore.setStatus(STATUS.VIRGIN);
            this.rewardForReviewStore.setError(null);
        });
    }
}

export default RewardForReviewService;
